import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { getCookie, setCookie } from 'cookies-next'
import { EmtTypography, EmtIcon } from 'emt-components-v2'

interface InfoTextBoxProps {
  infoText: React.ReactNode
  color?: 'purple'
  iconClassName?: string
  sx?: object
  closable?: boolean
  onClose?: () => void

  cookieKey?: string
  closeDuration?: number // ms
}

export const InfoTextBox: React.FC<InfoTextBoxProps> = ({
  infoText,
  color,
  iconClassName,
  sx,
  closable = false,
  onClose,
  cookieKey,
  closeDuration,
}) => {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    if (!cookieKey || !closeDuration) return

    const hiddenUntil = getCookie(cookieKey)

    if (hiddenUntil && Date.now() < Number(hiddenUntil)) {
      setIsVisible(false)
    }
  }, [cookieKey, closeDuration])

  if (!infoText || !isVisible) return null

  const handleClose = () => {
    setIsVisible(false)

    if (cookieKey && closeDuration) {
      const expiresAt = Date.now() + closeDuration

      setCookie(cookieKey, String(expiresAt), {
        maxAge: closeDuration / 1000, // cookies-next uses seconds
        path: '/',
      })
    }

    onClose?.()
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        backgroundColor: color === 'purple' ? '#AE59C6' : '#F9F9F9',
        padding: '16px',
        paddingRight: closable ? '32px' : '16px',
        borderRadius: '18px',
        border: color === 'purple' ? '2px solid #8A35A2' : 'none',
        ...sx,
      }}
    >
      <EmtIcon
        className={iconClassName || 'emt-info-circle-1'}
        sx={{
          fontSize: '20px!important',
          color: color === 'purple' ? '#fff' : 'inherit',
          backgroundColor: color === 'purple' ? '#8A35A2' : 'transparent',
          borderRadius: '50%',
        }}
      />

      {typeof infoText === 'string' ? (
        <EmtTypography
          variant="body2"
          sx={{
            fontSize: '12px',
            '& span': {
              color: 'primary.main',
              fontWeight: 700,
            },
          }}
        >
          {infoText}
        </EmtTypography>
      ) : (
        infoText
      )}

      {closable && (
        <EmtIcon
          className="emt-x"
          sx={{
            fontSize: '12px!important',
            cursor: 'pointer',
            position: 'absolute',
            top: '8px',
            right: '8px',
          }}
          onClick={handleClose}
        />
      )}
    </Box>
  )
}
